import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, Bug, Search } from 'lucide-react';
import { Button } from '../Common/button';
import { openBetterbugsWidget, setBetterbugsLanguage, setBetterbugsUser } from '../../lib/betterbugs';
import { setFullstoryUser } from '../../lib/fullstory';
import { useTourStore } from '../../stores/tourStore';
import { isPathAllowedInTour } from '../../config/onboardingTours';
import { BreadcrumbItem } from '../../hooks/useBreadcrumbs';
import { cn } from '../../lib/utils';
import { useImpersonation, useEffectiveUser } from '../../stores/authStore';
import { useBrandingStore } from '../../stores/brandingStore';
import { useOrgTagline } from '../../hooks/useAppName';
import { getApiBaseUrl } from '../../services/apiClient';
import { useUnreadCount } from '../../stores/notificationStore';
import { NotificationPanel } from '../Notifications/NotificationPanel';
import { HelpMenu } from './HelpMenu';
import { ToggleTheme } from './toogle-theme';
import { useLayoutStore } from '../../stores/layoutStore';
import { OrganizationSwitcher } from './OrganizationSwitcher';
import { TOPBAR_FIELD } from './topbarField';

interface AppHeaderProps {
  className?: string;
  breadcrumbs?: BreadcrumbItem[];
  orgName?: string;
  onStartTour?: (tourId: string) => void;
  onSearchOpen?: () => void;
  isSuperAdmin?: boolean;
}

export const AppHeader: React.FC<AppHeaderProps> = ({ className, breadcrumbs = [], orgName, onSearchOpen, isSuperAdmin = false }) => {
  const activeTourId = useTourStore((s) => s.activeTourId);
  const stepIndex = useTourStore((s) => s.stepIndex);
  const [notifOpen, setNotifOpen] = useState(false);
  const effectiveUser = useEffectiveUser();
  const userId = effectiveUser?.id ?? null;
  const unreadCount = useUnreadCount(userId, isSuperAdmin);
  const { impersonatedUser, isImpersonating, realUser, stopImpersonation } = useImpersonation();
  // The switcher moves the real user; hidden while acting as someone else (backend refuses too).
  const showOrgSwitcher = isSuperAdmin && !isImpersonating;
  const rawBrandingLogoUrl = useBrandingStore((s) => s.branding?.logo_url) ?? null;
  // logo_url is a relative API path; resolve it against the API origin so the
  // <img> hits the backend (the Vite dev proxy doesn't forward /organizations).
  const brandingLogoUrl = rawBrandingLogoUrl ? `${getApiBaseUrl()}${rawBrandingLogoUrl}` : null;
  // ui_settings.tagline takes the sub-brand line when set; empty keeps today's text.
  const tagline = useOrgTagline();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const setPageHeaderSlot = useLayoutStore((s) => s.setPageHeaderSlot);
  const hasPageHeader = useLayoutStore((s) => s.pageHeaderCount > 0);

  useEffect(() => {
    if (effectiveUser?.email) setBetterbugsUser(effectiveUser.email);
    if (effectiveUser?.id) setFullstoryUser();
  }, [effectiveUser?.id, effectiveUser?.email]);

  useEffect(() => {
    setBetterbugsLanguage(i18n.language);
  }, [i18n.language]);

  const handleStopImpersonation = () => {
    stopImpersonation();
    navigate('/welcome', { replace: true });
  };

  const isTourActive = activeTourId != null;
  const isNavDisabled = (path: string) =>
    isTourActive && !isPathAllowedInTour(path, activeTourId!, stepIndex);

  return (
    <>
      {isImpersonating && impersonatedUser && realUser && (
        <div
          className="bg-amber-500/15 border-b border-amber-500/40 flex items-center justify-center gap-3 py-2 px-4 text-sm"
          role="status"
        >
          <span className="text-foreground font-medium">
            {t('admin.impersonation.connectedAs', {
              name: impersonatedUser.name || impersonatedUser.email,
              realName: realUser.name || realUser.email,
            })}
          </span>
          <Button variant="outline" size="sm" onClick={handleStopImpersonation}>
            {t('admin.impersonation.stop')}
          </Button>
        </div>
      )}
      <header
        className={cn(
          'bg-card/95 border-b border-border flex items-center justify-between w-full',
          'backdrop-blur-sm',
          className
        )}
        style={{ paddingTop: '0.6rem', paddingBottom: '0.6rem' }}
      >
        {/* Left side - Logo with organization name (or "by GenieFactory") as subtitle */}
        {/* Same width as the expanded AppSidebar so the page title lines up with the page content */}
        <div className="flex items-start gap-0 px-6 w-[13rem] flex-shrink-0 flex-col">
          {(() => {
            const logoSrc = brandingLogoUrl || '/logo.png';
            const logoAlt = brandingLogoUrl ? orgName || t('global.organization') : 'GenieFactory';
            const logoImg = (cls: string) => (
              <img src={logoSrc} alt={logoAlt} className={cls} />
            );
            return isNavDisabled('/welcome')
              ? logoImg('h-10 opacity-50 cursor-not-allowed')
              : (
                <Link to="/welcome" className="block">
                  {logoImg('h-10 hover:opacity-80 transition-opacity')}
                </Link>
              );
          })()}
          {brandingLogoUrl ? (
            <span
              className="font-medium uppercase tracking-wider text-muted-foreground/80 truncate max-w-full leading-none"
              style={{ fontSize: '8px', marginTop: '2px' }}
              title={tagline ?? t('branding.poweredBy', 'by GenieFactory')}
            >
              {tagline ?? t('branding.poweredBy', 'by GenieFactory')}
            </span>
          ) : (
            (tagline || orgName) && (
              <span
                className="font-medium uppercase tracking-wider text-muted-foreground/80 truncate max-w-full"
                style={{
                  fontSize: '8px',
                  // Tucked under the wordmark of the default logo (no gap in the column).
                  marginTop: '-12px',
                  marginLeft: '40px',
                  marginBottom: '-5px',
                }}
                title={tagline ?? orgName}
              >
                {tagline ?? orgName}
              </span>
            )
          )}
        </div>

        {/* Middle — the page's PageLayout header is portaled into the slot; breadcrumbs are the
            fallback for pages without one. Crumbs are content-sized; long labels cap with ellipsis. */}
        <div className="flex items-center flex-1 min-w-0 pl-6 pr-6 overflow-hidden">
          <div ref={setPageHeaderSlot} className={cn('min-w-0', !hasPageHeader && 'hidden')} />
          <nav
            className={cn('flex items-center gap-1.5 text-sm text-muted-foreground min-w-0', hasPageHeader && 'hidden')}
            aria-label={t('breadcrumb.ariaLabel')}
          >
            {breadcrumbs.map((seg, index) => {
              const isLast = index === breadcrumbs.length - 1;
              const disabled = seg.href ? isNavDisabled(seg.href) : false;
              const Icon = seg.icon;

              const inner = (
                <>
                  {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" />}
                  <span className="truncate min-w-0">{seg.label}</span>
                </>
              );

              return (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <span className="text-muted-foreground/50 flex-shrink-0" aria-hidden>
                      /
                    </span>
                  )}
                  <div className="flex min-w-0 max-w-[min(28vw,14rem)] sm:max-w-[min(26vw,18rem)] shrink items-center">
                    {seg.href && !isLast && !disabled ? (
                      <Link
                        to={seg.href}
                        className="flex min-w-0 w-full items-center gap-1 hover:text-foreground hover:underline transition-colors"
                        title={seg.label}
                      >
                        {inner}
                      </Link>
                    ) : (
                      <span
                        className={cn(
                          'flex min-w-0 w-full items-center gap-1',
                          isLast ? 'text-foreground font-medium' : 'text-muted-foreground'
                        )}
                        title={seg.label}
                      >
                        {inner}
                      </span>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </nav>
        </div>

        {/* Right side - Search → Bug → Theme → Notifications → Help (left to right) */}
        <div className="flex items-center gap-1 pr-6 flex-shrink-0">
          {onSearchOpen && (
            <button
              onClick={onSearchOpen}
              className={cn(TOPBAR_FIELD, 'text-muted-foreground')}
              title={t('layout.searchTitle')}
            >
              <Search className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="text-xs">{t('layout.search')}</span>
              <kbd className="ml-auto text-[10px] px-1.5 border border-border rounded bg-background">⌘K</kbd>
            </button>
          )}
          {onSearchOpen && (
            <Button variant="ghost" size="icon" className="md:hidden h-9 w-9" onClick={onSearchOpen} title={t('layout.search')}>
              <Search className="h-4 w-4" />
            </Button>
          )}
          {showOrgSwitcher && <OrganizationSwitcher orgName={orgName} />}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={openBetterbugsWidget}
            title={t('bugReport.buttonTitle')}
          >
            <Bug className="h-4 w-4 text-primary" />
          </Button>
          <ToggleTheme iconOnly />
          <Button
            variant="ghost"
            size="icon"
            className="relative h-9 w-9"
            onClick={() => setNotifOpen(true)}
          >
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>
          <HelpMenu />
        </div>
      </header>

      {userId && (
        <NotificationPanel
          open={notifOpen}
          onOpenChange={setNotifOpen}
          userId={userId}
          isSuperAdmin={isSuperAdmin}
        />
      )}
    </>
  );
};
